<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import {
        Button,
        NotificationModal,
        PageLoading,
        SearchInput,
        SearchSelect,
        SvelteTable,
    } from "../../Components/Common/index.svelte";

    import { isMobile } from "../../stores";

    import {
        decodeSearchParams,
        encodeSearchParams,
        getAnnouncementsType,
        getFormatDate,
        getText,
        goToURL,
        replaceURL,
        showException,
    } from "../../Utils/utilFunctions.svelte";

    import { announcementsType } from "../../Utils/utilEnums.svelte";

    import { deleteIcon, editIcon } from "../../Utils/utilConstants.svelte";

    import TableFooter from "../../Components/Layout/Table/TableFooter.svelte";

    export let params;

    let isGettingAnnouncements = false,
        returnedAnnouncements = [];

    let createAnnouncementModalOpen = false,
        isCreatingAnnouncement = false,
        newAnnouncementObj = { type: 0 };

    let deleteAnnouncementModalOpen = false,
        selectedAnnouncementID = Number(0),
        isDeletingAnnouncement = false;

    let filters = { type: 0 };

    let page = 0,
        pageSize = 25,
        count;

    const columns = [
        {
            key: "id",
            title: "ID",
            headerClass: "text-left px-3 ml-3 cursor-default rounded-tl-xl",
            class: "text-left px-3 cursor-pointer",
            value: (v) => getText(v.id),
        },
        {
            key: "type",
            title: "Type",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => getAnnouncementsType(v.type),
        },
        {
            key: "subject",
            title: "Subject",
            headerClass: "text-left px-3 cursor-default w-1/3",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => getText(v.subject),
        },
        {
            key: "createdDate",
            title: "Created Date",
            headerClass: "text-left px-3 cursor-default",
            class: "text-left pl-3 cursor-pointer",
            value: (v) => getFormatDate(v.createdDate),
        },
        {
            key: "buttons",
            title: "Actions",
            headerClass: "text-left pl-3 cursor-default rounded-tr-xl w-[2%]",
            class: "px-3 cursor-pointer",
            renderValue: (v) => _renderActionIcons(),
            csvExportHidden: true,
        },
    ];

    onMount(() => {
        if (params.query) _updateSearchParamsByQuery(params.query);

        loadAnnouncements(page * pageSize, pageSize, _getSearchParams());
    });

    const loadAnnouncements = async (start, limit, params) => {
        isGettingAnnouncements = true;

        await apiMethods.getMethod
            .getAnnouncements(start, limit, params)
            .then((res) => _handleAnnouncements(res))
            .catch((err) => showException(err));

        isGettingAnnouncements = false;
    };

    const createAnnouncement = async () => {
        isCreatingAnnouncement = true;

        await apiMethods.postMethod
            .createAnnouncement(newAnnouncementObj)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        "Failed to create announcement, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    loadAnnouncements(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
            })
            .catch((err) => showException(err));

        _closeCreateAnnouncementModal();

        isCreatingAnnouncement = false;
    };

    const deleteAnnouncement = async () => {
        isDeletingAnnouncement = true;

        await apiMethods.deleteMethod
            .deleteAnnouncement(selectedAnnouncementID)
            .then((res) => {
                if (!res || !res.id)
                    showError(
                        "Failed to delete announcement, please refresh the page and try again and contact support if the issue persists.",
                    );
                else
                    loadAnnouncements(
                        page * pageSize,
                        pageSize,
                        _getSearchParams(),
                    );
            })
            .catch((err) => showException(err));

        _closeDeleteAnnouncementModal();

        isDeletingAnnouncement = false;
    };

    const handleFilterSelect = () => {
        page = 0;

        updateURL();

        loadAnnouncements(0, pageSize, _getSearchParams());
    };

    const updateURL = () => {
        replaceURL(
            `/Announcements/page=${page}&size=${pageSize}${_getParamsString()}`,
        );
    };

    const handleClickCell = (event, rowData, key) => {
        const { className } = event.target;

        if (key == "buttons") {
            if (className.includes("deleteIcon"))
                _openDeleteAnnouncementModal(rowData.id);
            else if (className.includes("editIcon"))
                goToURL(`/Announcements/edit/${rowData.id}`);
        } else goToURL(`/Announcements/view/${rowData.id}`);
    };

    const handleClickCol = (event, colData, key) => {};

    const onPageChange = (e) => {
        page = Number(e.detail.page);

        updateURL();

        loadAnnouncements(
            Number(e.detail.firstDataIndex),
            pageSize,
            _getSearchParams(),
        );
    };

    const onPageSizeChange = (newPageSize) => {
        pageSize = newPageSize;
        page = 0;

        updateURL();

        loadAnnouncements(0, pageSize, _getSearchParams());
    };

    // HELPER FUNCTIONS
    const _handleAnnouncements = (res) => {
        if (!res)
            showError(
                "Failed to load announcements data, please refresh the page and try again and contact support if the issue persists.",
            );
        else if (res.error) showError(res.error);
        else {
            returnedAnnouncements = [...res.results];
            count = res.total;
        }
    };

    const _updateSearchParamsByQuery = (query) => {
        const arr = decodeSearchParams(query).split("&");

        if (arr.length) {
            for (let i = 0; i < arr.length; i++) {
                if (arr[i].includes("page="))
                    page = Number(arr[i].split("=")[1]);

                if (arr[i].includes("size="))
                    pageSize = Number(arr[i].split("=")[1]);

                if (arr[i].includes("search="))
                    filters.search = arr[i].split("=")[1].toString();

                if (arr[i].includes("type="))
                    filters.type = Number(arr[i].split("=")[1]);
            }

            updateURL();
        }
    };

    const _getSearchParams = () => {
        let params = {};

        if (filters.search) params.search = filters.search;

        if (filters.type || filters.type == Number(0))
            params["filter[type]"] = filters.type;

        return params;
    };

    const _getParamsString = () => {
        let str = "";

        const params = _getSearchParams();

        if (params) {
            if (params.search) str += `&search=${params.search}`;

            if (params["filter[type]"] || params["filter[type]"] == Number(0))
                str += `&type=${params["filter[type]"]}`;
        }

        return encodeSearchParams(str);
    };

    const _renderActionIcons = () => {
        return `<div class="flex flex-1 items-center justify-start gap-x-2">
                    <div class="editIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Edit Announcement">
                        <i class="editIcon ${editIcon}"></i>
                    </div>

                    <div class="deleteIcon tooltip tooltip-secondary tooltip-left flex items-center justify-center w-[30px] h-[30px] hover:rounded-full hover:bg-secondary hover:text-white" data-tip="Delete Announcement">
                        <i class="deleteIcon ${deleteIcon}"></i>
                    </div>
                </div>`;
    };

    const _openCreateAnnouncementModal = () =>
        (createAnnouncementModalOpen = true);

    const _closeCreateAnnouncementModal = () => {
        createAnnouncementModalOpen = false;

        newAnnouncementObj = { type: 0 };
    };

    const _openDeleteAnnouncementModal = (id) => {
        deleteAnnouncementModalOpen = true;

        selectedAnnouncementID = Number(id);
    };

    const _closeDeleteAnnouncementModal = () => {
        deleteAnnouncementModalOpen = false;

        selectedAnnouncementID = Number(0);
    };
</script>

<div class="flex flex-col flex-1 gap-y-4">
    <div
        class="flex flex-0 {$isMobile
            ? 'flex-col gap-y-4 items-start'
            : 'justify-between items-end'}"
    >
        <div class="flex gap-4 flex-wrap">
            <SearchInput
                bind:value={filters.search}
                title="Search"
                inputFunc={handleFilterSelect}
                isLong
            />

            <SearchSelect
                bind:value={filters.type}
                title="Type"
                onChangeFunc={handleFilterSelect}
            >
                {#each announcementsType as type}
                    <option value={type.id} selected={filters.type == type.id}>
                        {type.type}
                    </option>
                {/each}
            </SearchSelect>
        </div>

        <div class="flex justify-end {$isMobile ? '' : 'max-w-[50%]'}">
            <Button clickFunc={_openCreateAnnouncementModal}>
                Create Announcement
            </Button>
        </div>
    </div>

    <div class={$isMobile ? "h-auto w-minus48" : "relative flex-1"}>
        {#if isGettingAnnouncements}
            <PageLoading />
        {:else}
            <div
                class={$isMobile
                    ? "overflow-x-auto overflow-y-visible"
                    : "overflow-y-auto absolute inset-0"}
            >
                <SvelteTable
                    {columns}
                    rows={returnedAnnouncements}
                    clickCellFunc={({ detail: { event, row, key } }) => {
                        handleClickCell(event, row, key);
                    }}
                    clickColFunc={({ detail: { event, row, key } }) => {
                        handleClickCol(event, row, key);
                    }}
                />
            </div>
        {/if}
    </div>

    <TableFooter
        {page}
        {pageSize}
        {count}
        tableData={returnedAnnouncements}
        {onPageChange}
        {onPageSizeChange}
        csvConfig={{
            columns,
            data: returnedAnnouncements,
            fileName: "Announcements",
            allDataApi: async () =>
                await apiMethods.getMethod.getAnnouncements(
                    0,
                    999999,
                    _getSearchParams(),
                ),
        }}
    />
</div>

<NotificationModal
    title="Create Announcement"
    submitText={isCreatingAnnouncement ? "Creating" : "Create"}
    isModalOpen={createAnnouncementModalOpen}
    isConfirming={isCreatingAnnouncement}
    closeModal={_closeCreateAnnouncementModal}
    confirmAction={createAnnouncement}
    closeModalFunction={_closeCreateAnnouncementModal}
>
    <div class="flex flex-col gap-y-4">
        <div class="flex gap-x-4">
            <p class="w-36">Type:</p>

            <select
                bind:value={newAnnouncementObj.type}
                class="input input-bordered input-sm w-full"
            >
                {#each announcementsType as type}
                    <option value={type.id}>{type.type}</option>
                {/each}
            </select>
        </div>

        <div class="flex gap-x-4">
            <p class="w-36">Subject:</p>

            <input
                type="text"
                bind:value={newAnnouncementObj.subject}
                class="input input-bordered input-sm w-full"
                autocomplete="off"
            />
        </div>

        <div class="flex gap-x-4">
            <p class="w-36">Content:</p>

            <textarea
                class="textarea textarea-bordered w-full"
                rows="3"
                autocomplete="off"
                bind:value={newAnnouncementObj.content}
            />
        </div>
    </div>
</NotificationModal>

<NotificationModal
    title="Delete Announcement"
    submitText={isDeletingAnnouncement ? "Deleting" : "Delete"}
    confirmButtonClass="btn-error"
    cancelButtonClass="btn-accent"
    isModalOpen={deleteAnnouncementModalOpen}
    isConfirming={isDeletingAnnouncement}
    closeModal={_closeDeleteAnnouncementModal}
    closeModalFunction={_closeDeleteAnnouncementModal}
    confirmAction={deleteAnnouncement}
>
    Delete the announcement ID: {selectedAnnouncementID}?
</NotificationModal>
